import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  deleteToken,
  Messaging,
} from "firebase/messaging";
import Config from "@/config";
import {firebaseApp} from "@/features/api";
import {useEffect, useState, useCallback, useMemo} from "react";
import {localDataStore, localDataGet} from "@/utils/localStorage";
import usePutUserSettings from "@/features/settings/hooks/usePutUserSettings";
import {UserNotificationSettings} from "@/features/settings/hooks/useGetUserSettings";
import {useEventListener} from "./useEventListener";
import {showToast} from "@/components/GlobalToastRegion/toast-queue";
import {isMobile} from "@/utils/detectors";
import {isErrorWithCode} from "@/utils/typeGuards";
import {useVisibilityChange} from "@uidotdev/usehooks";

// Initialize Firebase Cloud Messaging and get a reference to the service.
// Module-scoped so every hook instance shares one Messaging object; the
// assignment lives in this module function rather than inside the hook, which
// must stay free of side effects on values outside its own scope.
let messaging:
  | (Messaging & {swRegistration?: ServiceWorkerRegistration})
  | null = null;

const initMessaging = () => {
  messaging ??= getMessaging(firebaseApp);
  return messaging;
};

export const usePushNotifications = () => {
  // Set active state based on the local storage data.
  // If the token is stored in the local storage, the push notifications are enabled.
  const [isEnabledLocal, setIsEnabledLocal] = useState(
    !!localDataGet("messagingToken"),
  );

  // State to indicate if there is a new message.
  const [messageCount, setMessageCount] = useState(0);

  const [isMessagingSupported, setIsMessagingSupported] = useState<
    boolean | null
  >(null);

  const documentVisible = useVisibilityChange();
  // `mutate` is referentially stable, unlike the mutation object around it,
  // so callbacks below can depend on it without re-creating every render.
  const {mutate: putUserSettingsMutate} = usePutUserSettings();

  const checkMessagingSupport = useCallback(async () => {
    try {
      const supported = await isSupported();
      setIsMessagingSupported(supported);

      // Initialize messaging if supported
      if (supported) {
        initMessaging();
      }

      // If not supported but enabled locally, disable it
      if (!supported && isEnabledLocal) {
        setIsEnabledLocal(false);
      }

      return supported;
    } catch (error) {
      console.error("Error checking messaging support:", error);
      setIsMessagingSupported(false);
      if (isEnabledLocal) {
        setIsEnabledLocal(false);
      }
      return false;
    }
  }, [isEnabledLocal]);

  useEffect(() => {
    checkMessagingSupport();
  }, [checkMessagingSupport]);

  // Get the current notification permission state. Memoised because the
  // promise identity is a dependency of useEventListener's effect, so building
  // it inline would re-attach the listener on every render. Null where the
  // Permissions API is missing; useEventListener treats that as "no target".
  const notificationPermission = useMemo(
    () =>
      "permissions" in navigator
        ? navigator.permissions.query({name: "notifications"})
        : null,
    [],
  );

  // Listen for the notifications permission change event.
  // If user denies the permission in a browser, disable the push notifications.
  useEventListener(
    "change",
    (event: Event) => {
      const {state} = event.target as PermissionStatus;

      // If the permission is denied, disable the push notifications.
      if (state === "denied") {
        setIsEnabledLocal(false);
      }

      // If the permission is granted, show the toast message.
      if (state === "granted") {
        showToast("Notifikace jsou zapnuté", "success");
      }
    },
    notificationPermission,
  );

  // Track if the document (app page) is visible.
  useEffect(() => {
    // If the document is visible and the push notifications are enabled,
    // but the permission is denied, disable the push notifications.
    // This state can occur when the user denies the permission in OS settings, ex. on iOS.
    if (
      documentVisible &&
      isEnabledLocal &&
      Notification.permission === "denied"
    ) {
      setIsEnabledLocal(false);
    }
  }, [documentVisible, isEnabledLocal]);

  useEffect(() => {
    if (!isMessagingSupported || !messaging) return;

    // Subscribe to the onMessage event to listen for incoming notifications,
    // when the app is in the foreground.
    const unsubscribe = onMessage(messaging, () => {
      setMessageCount(messageCount + 1);
    });

    return () => {
      // Unsubscribe from the onMessage event.
      unsubscribe();
    };
  }, [setMessageCount, messageCount, isMessagingSupported]);

  // Delete the token from the Firebase Cloud Messaging.
  const deleteMessagingToken = useCallback(async () => {
    // Check if the service worker registration is found.
    // deleteToken() requires the service worker registration.
    if (messaging && messaging.swRegistration) {
      try {
        await deleteToken(messaging);
      } catch (error) {
        console.error("Error deleting messaging token:", error);
      }
    }
  }, []);

  // Send the token to the server to enable push notifications.
  const sendTokenToServer = useCallback(
    (token: string) => {
      if (!token) return;

      // Time to live for the token in the local storage.
      const ttl = 60 * 60 * 24 * 21; // 21 days

      // Get the token from the local storage.
      const messagingToken = localDataGet("messagingToken");

      // Store the token in the local storage with a time to live.
      if (!messagingToken) localDataStore("messagingToken", token, ttl);

      // If the token is already stored in the local storage, don't send it to the server.
      if (messagingToken === token) return;

      // Data to be sent to the server.
      const notificationSettings = [
        {
          type: "push",
          object: token,
          enabled: true,
        } as UserNotificationSettings,
      ];

      putUserSettingsMutate({notificationSettings});
    },
    [putUserSettingsMutate],
  );

  // Reset enabled state after 1 second for better UX.
  const resetEnabledState = useCallback(() => {
    setTimeout(() => {
      setIsEnabledLocal(false);
    }, 1000);
  }, []);

  // Get the Firebase Cloud Messaging token.
  const getMessagingToken = useCallback(async () => {
    // Ensure messaging support is available
    if (isMessagingSupported === null) {
      const supported = await checkMessagingSupport();
      if (!supported || !messaging) {
        showToast("Notifikace nejsou podporované v tomto prohlížeči");
        resetEnabledState();
        return;
      }
    } else if (!isMessagingSupported || !messaging) {
      showToast("Notifikace nejsou podporované v tomto prohlížeči");
      resetEnabledState();
      return;
    }

    // Get the service worker registration.
    const serviceWorkerRegistration =
      await window.navigator.serviceWorker.getRegistration();

    // Check if the service worker registration is found.
    if (!serviceWorkerRegistration) {
      console.error("Service worker registration not found.");
      resetEnabledState();
      return;
    }

    // Check if the service worker is active.
    if (!serviceWorkerRegistration.active) {
      console.error("Service worker not active.");
      resetEnabledState();
      return;
    }

    // Get the current Firebase Cloud Messaging token.
    try {
      const currentMessagingToken = await getToken(messaging, {
        vapidKey: Config.FIREBASE_VAPID_KEY,
        serviceWorkerRegistration,
      });

      if (currentMessagingToken) sendTokenToServer(currentMessagingToken);
    } catch (error) {
      resetEnabledState();

      const code = isErrorWithCode(error) ? error.code : undefined;

      if (code === "messaging/permission-blocked") {
        showToast("Notifikace jsou blokované prohlížečem", "warning");
        return;
      }

      console.error(
        "An error occured while retrieving messaging token.",
        error,
      );
      showToast(`Notifikace nelze zapnout kvůli chybě: ${code}`, "error");
    }
  }, [
    isMessagingSupported,
    checkMessagingSupport,
    sendTokenToServer,
    resetEnabledState,
  ]);

  useEffect(() => {
    if (isMessagingSupported === null) return; // Wait for support check to complete

    if (!isMessagingSupported || !isEnabledLocal) {
      if (localDataGet("messagingToken")) {
        window.localStorage.removeItem("messagingToken");
      }

      if (isMessagingSupported && messaging) {
        deleteMessagingToken();
      }
      return;
    }

    getMessagingToken();
  }, [
    isEnabledLocal,
    isMessagingSupported,
    deleteMessagingToken,
    getMessagingToken,
  ]);

  const setIsEnabled = useCallback(
    async (enabled: boolean) => {
      // If enabling, ensure messaging is supported
      if (enabled) {
        if (isMessagingSupported === null) {
          // Check support if not determined yet
          const supported = await checkMessagingSupport();
          if (!supported) {
            showToast(
              "Notifikace nejsou podporované v tomto prohlížeči",
              "warning",
            );
            return;
          }
        } else if (!isMessagingSupported) {
          showToast(
            "Notifikace nejsou podporované v tomto prohlížeči",
            "warning",
          );
          return;
        }
      }

      setIsEnabledLocal(enabled);

      // If the permission is denied, don't show the toast.
      if (Notification?.permission !== "granted") return;

      // Don't show the toast on mobile.
      if (isMobile) return;

      if (enabled) {
        showToast("Notifikace jsou zapnuté", "success");
      } else {
        showToast("Notifikace jsou vypnuté");
      }
    },
    [isMessagingSupported, checkMessagingSupport],
  );

  // Memoized so consumers can keep this object in a dependency array: AppLayout
  // renders its header from a useMemo keyed on it, and a fresh literal here
  // would make that memo recompute on every render.
  return useMemo(
    () => ({isEnabled: isEnabledLocal, setIsEnabled, messageCount}),
    [isEnabledLocal, setIsEnabled, messageCount],
  );
};
